import { FMSHelper } from "@swivl-mono/helpers";
import {
  AccessCodeRequestResponse,
  AccessCodeRequestResponseStatus,
  FMSCreateLeadResponse,
  FMSError,
  FMSInterface,
  FMSJitSettingsResponse,
  FMSJitSettingsUpdateRequest,
  FMSPaymentInterface,
  FMSRoute,
  FMSServiceReturnData,
  FMSServiceReturnDataStatus,
  FMSType,
  FMSValidAuth,
  FacilityInterface,
  GetAccessCodeParams,
  GetEntityDependenciesParams,
  GetLeadsParams,
  GetLeasesParams,
  GetTenantParams,
  GetTenantResponse,
  GetTenantsParams,
  GetUnitsParams,
  LeaseAccessCodeResult,
  LocationVarKey,
  NormalizedEntityDependencies,
  NormalizedFMSLeadInterface,
  NormalizedFMSLeaseInterface,
  NormalizedFMSTenantInterface,
  NormalizedFMSUnitInterface,
  NormalizedMakePaymentResponse,
  PaymentSessionStatus,
  RentalLead,
  UnitInterface,
  VarKey
} from "@swivl-mono/types";

import { API } from "../API";
import { APIService } from "../Service/API.Service";

// LINK apps/server/src/API/FMS/FMS.Controller.ts

export interface FMSServiceProviderInterface {
  // LINK libs/server-helpers/src/FMS/FMSs/FMS.Service.ts
  getUnits(fms: FMSType, auth: FMSValidAuth, params: GetUnitsParams): Promise<FMSServiceReturnData<NormalizedFMSUnitInterface[]>>;
  getTenants(fms: FMSType, auth: FMSValidAuth, params: GetTenantsParams): Promise<FMSServiceReturnData<NormalizedFMSTenantInterface[]>>;
  getLeases(fms: FMSType, auth: FMSValidAuth, params: GetLeasesParams): Promise<FMSServiceReturnData<NormalizedFMSLeaseInterface[]>>;
  getLeads(fms: FMSType, auth: FMSValidAuth, params: GetLeadsParams): Promise<FMSServiceReturnData<NormalizedFMSLeadInterface[]>>;
  createLead(
    fms: FMSType,
    auth: FMSValidAuth,
    lead: Partial<RentalLead>,
    facility: FacilityInterface
  ): Promise<FMSServiceReturnData<FMSCreateLeadResponse>>;
  getTenant(fms: FMSType, auth: FMSValidAuth, params: GetTenantParams): Promise<FMSServiceReturnData<GetTenantResponse>>;
  getAccessCode(fms: FMSType, auth: FMSValidAuth, params: GetAccessCodeParams): Promise<FMSServiceReturnData<AccessCodeRequestResponse>>;
  makePayments(
    fms: FMSType,
    auth: FMSValidAuth,
    payment: FMSPaymentInterface,
    facilityId: string | undefined
  ): Promise<FMSServiceReturnData<NormalizedMakePaymentResponse>>;
  getEntityDependencies(fms: FMSType, auth: FMSValidAuth, params: GetEntityDependenciesParams): NormalizedEntityDependencies;
}

/**
 * The Outbound Gate's FMS writer (ENG-3373), injected by every app that also injects the FMS
 * provider (`FmsWrite` from server-helpers). The api-helper cannot import server-helpers, so the
 * shape is structural. Writes through `API.FMS` refuse to run without it — fail closed.
 */
export interface FmsWriteGateInterface {
  createLead(
    org: number,
    leadPhone: string | null | undefined,
    fms: FMSType,
    auth: FMSValidAuth,
    lead: Partial<RentalLead>,
    facility: FacilityInterface
  ): Promise<{ result: "real"; response: FMSServiceReturnData<FMSCreateLeadResponse> } | { result: "simulated"; decision: { reason: string } }>;
  makePayments(
    org: number,
    tenantId: number | null | undefined,
    fms: FMSType,
    auth: FMSValidAuth,
    payment: FMSPaymentInterface,
    facilityId: string
  ): Promise<FMSServiceReturnData<NormalizedMakePaymentResponse>>;
}

export class FMSAPI {
  static async getTenants(
    path: { fms: FMSType; facilityId: string },
    params: GetTenantsParams,
    body: { auth: FMSValidAuth }
  ): Promise<FMSServiceReturnData<NormalizedFMSTenantInterface[]>> {
    const tenantsParams: GetTenantsParams = {
      ...(params ? params : {}),
      facilityId: path.facilityId
    };

    return APIService.FMSServiceProvider.getTenants(path.fms, body?.auth, tenantsParams);
  }

  static async getUnits(
    path: { fms: FMSType; facilityId: string; fmsId: number },
    params: GetUnitsParams,
    body: { auth: FMSValidAuth }
  ): Promise<FMSServiceReturnData<NormalizedFMSUnitInterface[]>> {
    const unitsParams: GetUnitsParams = {
      ...(params ? params : {}),
      facilityId: path.facilityId
    };

    return APIService.FMSServiceProvider.getUnits(path.fms, body?.auth, unitsParams);
  }

  static async getUnitsDB(orgId: number, locationId: number, params?: { available?: boolean }): Promise<UnitInterface[]> {
    const units = await API.Units.getUnitsByLocationId(orgId, locationId, params);
    const sortedUnits: UnitInterface[] = FMSHelper.sortUnits(units);
    return sortedUnits;
  }

  static async getLeases(
    path: { fms: FMSType; facilityId: string; orgId?: number; locationId?: number },
    params: GetLeasesParams,
    body: { auth: FMSValidAuth }
  ): Promise<FMSServiceReturnData<NormalizedFMSLeaseInterface[]>> {
    const leasesParams: GetLeasesParams = {
      ...(params ? params : {}),
      facilityId: path.facilityId,
      orgId: path.orgId,
      locationId: path.locationId
    };

    return APIService.FMSServiceProvider.getLeases(path.fms, body?.auth, leasesParams);
  }

  static async getLeads(
    path: { fms: FMSType; facilityId: string },
    params: GetLeadsParams,
    body: { auth: FMSValidAuth }
  ): Promise<FMSServiceReturnData<NormalizedFMSLeadInterface[]>> {
    const leadsParams: GetLeadsParams = {
      ...(params ? params : {}),
      facilityId: path.facilityId
    };

    return APIService.FMSServiceProvider.getLeads(path.fms, body?.auth, leadsParams);
  }

  static getEntityDependencies(
    path: { fms: FMSType },
    params: GetEntityDependenciesParams,
    body: { auth: FMSValidAuth }
  ): NormalizedEntityDependencies {
    return APIService.FMSServiceProvider.getEntityDependencies(path.fms, body?.auth, params);
  }

  /** Through the Outbound Gate: the org's FMS flag, then outside production the allowlist on the lead's phone. */
  static async createLead({
    orgId,
    fms,
    auth,
    lead,
    facility
  }: {
    orgId: number;
    fms: FMSType;
    auth: FMSValidAuth;
    lead: Partial<RentalLead>;
    facility: FacilityInterface;
  }): Promise<FMSServiceReturnData<FMSCreateLeadResponse>> {
    if (!auth || !fms) {
      throw new Error("Missing createLead params");
    }
    if (!APIService.FmsWrite) {
      throw new Error("FMS writes need the Outbound Gate: API.init({ FmsWrite }) was not called");
    }

    let createLeadResponse: FMSServiceReturnData<FMSCreateLeadResponse> = {};

    try {
      const written = await APIService.FmsWrite.createLead(orgId, lead[VarKey.UserPhoneNumber], fms, auth, lead, facility);
      createLeadResponse =
        written.result === "real"
          ? written.response
          : { status: FMSServiceReturnDataStatus.SIMULATED, error: { message: `simulated:${written.decision.reason}` } as FMSError };
    } catch (e) {
      console.warn("Error creating lead", e);
      createLeadResponse = {
        error: {
          message: (e as FMSError)?.message,
          status: (e as FMSError)?.status
        },
        status: FMSServiceReturnDataStatus.FAILURE
      };
    }

    return createLeadResponse;
  }

  static async getTenant({
    routeTo,
    liveParams,
    dbParams
  }: {
    routeTo: FMSRoute;
    liveParams?: { fms: FMSType; auth: FMSValidAuth; params: GetTenantParams };
    dbParams?: {
      userId: number;
      locationId: number;
      identifiers?: { tenantId?: string; emails?: string[]; phoneNumbers?: string[] };
    };
  }): Promise<FMSServiceReturnData<GetTenantResponse>> {
    if (routeTo === FMSRoute.DATABASE) {
      // if(identifiers?.tenantId && locationId){
      //   return API.Tenants.getTenantByExternalTenantId(fms.orgId, locationId, identifiers.tenantId);
      // }
      // if(userId) {
      // }
    }

    if (routeTo === FMSRoute.LIVE) {
      if (!liveParams?.auth || !liveParams?.fms || !liveParams?.params.facilityId) {
        throw new Error("Missing getTenant params");
      }

      let tenantResponse: FMSServiceReturnData<GetTenantResponse>;

      try {
        tenantResponse = await APIService.FMSServiceProvider.getTenant(liveParams.fms, liveParams.auth, liveParams.params);
      } catch (e) {
        tenantResponse = {
          error: {
            message: (e as FMSError)?.message,
            status: (e as FMSError)?.status
          }
        };
      }

      return tenantResponse;
    }

    return {};
  }

  static async getAccessCode({
    fms,
    auth,
    params
  }: {
    fms: FMSType;
    auth: FMSValidAuth;
    params: GetAccessCodeParams;
  }): Promise<FMSServiceReturnData<AccessCodeRequestResponse>> {
    if (!auth || !fms) {
      throw new Error("Missing getAccessCode params");
    }

    let accessCodeResponse: FMSServiceReturnData<AccessCodeRequestResponse> = {};

    try {
      accessCodeResponse = await APIService.FMSServiceProvider.getAccessCode(fms, auth, params);
    } catch (e) {
      accessCodeResponse = {
        error: {
          message: (e as FMSError)?.message,
          status: (e as FMSError)?.status
        }
      };
    }

    return accessCodeResponse;
  }

  static async makePayments(
    sessionId: string,
    signature: string,
    payment: FMSPaymentInterface
  ): Promise<FMSServiceReturnData<NormalizedMakePaymentResponse>> {
    if (!sessionId) {
      throw new Error("Session ID is required to make a payment.");
    }
    let paymentSession = await API.Payments.getPaymentSession(sessionId);
    if (!paymentSession) {
      throw new Error("Payment session not found.");
    }
    paymentSession = await API.Payments.updatePaymentSession(sessionId, { paymentLink: null });

    if (paymentSession.status !== PaymentSessionStatus.OPENED) {
      throw new Error("Wrong payment session status.");
    }
    if (new Date(paymentSession.expiresAt) < new Date()) {
      throw new Error("Payment session has expired.");
    }
    if (!signature) {
      throw new Error("Signature is required to make a payment.");
    }
    const isSignatureValid = await API.Payments.verifyPaymentSessionSignature(sessionId, signature);
    if (!isSignatureValid) {
      throw new Error("Invalid signature.");
    }
    if (!payment.cardToken) {
      throw new Error("Card token is required to make a payment.");
    }
    const locationId = paymentSession.paymentSessionLeases?.[0]?.locationId;
    if (!locationId) {
      throw new Error("No location found for payment session leases");
    }
    const fms = await this.getFMSForLocation(paymentSession.orgId, locationId);
    if (!fms) {
      throw new Error("No FMS found for location");
    }

    const fmsType = fms.type as FMSType;
    const auth = FMSHelper.attachLocationContextToToken(fmsType, fms.token, paymentSession.orgId, locationId) as FMSValidAuth;
    const locationVars = await API.LocationVars.getVars([LocationVarKey.FMSId], paymentSession.orgId, undefined, locationId);
    const facilityId = locationVars[LocationVarKey.FMSId] || undefined;
    if (!facilityId) {
      throw new Error("No FMS facility ID found for location");
    }

    let makePaymentsResponse: FMSServiceReturnData<NormalizedMakePaymentResponse> = {};

    if (!APIService.FmsWrite) {
      throw new Error("FMS writes need the Outbound Gate: API.init({ FmsWrite }) was not called");
    }
    // The Outbound Gate on the session's tenant (ENG-3373): not allowed throws — a payment is never simulated.
    const tenantId = paymentSession.paymentSessionLeases?.[0]?.tenantId ?? null;
    try {
      makePaymentsResponse = await APIService.FmsWrite.makePayments(paymentSession.orgId, tenantId, fmsType, auth, payment, facilityId);
      if (makePaymentsResponse?.status === FMSServiceReturnDataStatus.SUCCESS) {
        await API.Payments.closePaymentSession(sessionId, signature, PaymentSessionStatus.COMPLETED);
      }
    } catch (e) {
      console.error(e);
      makePaymentsResponse = {
        status: FMSServiceReturnDataStatus.FAILURE
      };
    }

    return makePaymentsResponse;
  }

  static async getFMSByOrgId(orgId: number) {
    return APIService.Server.get<FMSInterface[]>(`/fms/org/${orgId}`);
  }

  static async getFMSForLocation(orgId: number, locationId?: number, isInternal?: boolean) {
    const payload = locationId ? { locationId: locationId } : undefined;
    return APIService.Server.get<FMSInterface>(`${isInternal ? "/fms/internal" : "/fms"}/org/${orgId}/for-location`, payload);
  }

  /** Live access-code lookup keyed by lease only — the server resolves the FMS
   *  integration and facility scope from the lease row. No cached fallback. */
  static async getAccessCodeForLease(orgId: number, leaseId: number) {
    return APIService.Server.get<LeaseAccessCodeResult>(`/fms/internal/org/${orgId}/lease/${leaseId}/access-code`);
  }

  static async getFMSByType(orgId: number, type: FMSType) {
    return APIService.Server.get<FMSInterface>(`/fms/type/${type}`);
  }

  static async getFMSById(orgId: number, fmsId: number) {
    return APIService.Server.get<FMSInterface>(`/fms/org/${orgId}/fms/${fmsId}`);
  }

  static async getSyncEnabledFMSs() {
    return APIService.Server.get<FMSInterface[]>(`/fms/sync-enabled`);
  }

  static async createFMS(orgId: number, fms: Partial<FMSInterface>) {
    return APIService.Server.post<FMSInterface>(`/fms/org/${orgId}/fms`, fms);
  }

  static async updateFMS(orgId: number, fmsId: number, fms: Partial<FMSInterface>) {
    return APIService.Server.patch<FMSInterface>(`/fms/org/${orgId}/fms/${fmsId}`, fms);
  }

  static async deleteFMS(orgId: number, fmsId: number) {
    return APIService.Server.delete<boolean>(`/fms/org/${orgId}/fms/${fmsId}`);
  }

  // ─── Sequences JIT freshness settings (ENG-3213) — per FMS integration ────

  static async getJitSettings(orgId: number, fmsId: number) {
    return APIService.Server.get<FMSJitSettingsResponse>(`/fms/org/${orgId}/fms/${fmsId}/jit-settings`);
  }

  /** MERGE per sequence type: present types replace their cell, omitted types are untouched. */
  static async updateJitSettings(orgId: number, fmsId: number, body: FMSJitSettingsUpdateRequest) {
    return APIService.Server.put<FMSJitSettingsResponse>(`/fms/org/${orgId}/fms/${fmsId}/jit-settings`, body);
  }

  static async getAccessCodeByQueryId(
    orgId: number,
    locationId: number,
    fmsData: FMSInterface,
    facilityId: string,
    accessCodeQueryId: string
  ): Promise<string | null> {
    const fms = fmsData.type as FMSType;
    const auth = fmsData.token as FMSValidAuth;
    const params: GetAccessCodeParams = { id: accessCodeQueryId, facilityId };

    if (!accessCodeQueryId) {
      return null;
    }

    const accessCodeResp = await FMSAPI.getAccessCode({ fms, auth, params });

    if (accessCodeResp?.items?.status === AccessCodeRequestResponseStatus.SUCCESS) {
      const accessCode: string | null = accessCodeResp?.items?.accessCode || null;

      if (accessCode) {
        return accessCode;
      }
    }

    return null;
  }

  static async getAccessCodesDB(orgId: number, userId: number): Promise<{ accessCodes: string[]; davinciCodes: string[] }> {
    const leases = await API.Leases.getLeasesByUserId(orgId, userId);
    if (leases?.length) {
      const accessCodes = leases.map((lease) => lease.accessCode).filter(Boolean) as string[];
      // const divinciCodes = leases.map(lease => lease.davinciCode);
      return { accessCodes, davinciCodes: [] };
    }
    return { accessCodes: [], davinciCodes: [] };
  }
}
